import type { LucideIcon } from "lucide-react";
import {
  Type,
  Heading,
  MousePointerClick,
  Square,
  Space,
  Minus,
  Megaphone,
  Code,
  Calendar,
  CalendarPlus,
  Package,
  MessageCircle,
  MessageSquare,
  FileText,
  ClipboardList,
  NotebookPen,
  UserPen,
  BookOpen,
  CreditCard,
  Upload,
  Zap,
  Activity,
  Receipt,
  Weight,
  ShoppingBag,
} from "lucide-react";
import { i18n } from "@tellescope/i18n";
import type { LeafBlock, AppointmentCategory, FormResponseCategory, ProfileFieldKey } from "../../../../shared/schema";
import { getBlockTypes, type BlockType, isContainerBlockType } from "../../../../shared/schema";

// Leaf block types (blocks that can be nested in sections - excludes container blocks)
// Derived from BLOCK_TYPES to prevent drift between the two lists
export const LEAF_BLOCK_TYPES: BlockType[] = getBlockTypes().reduce<BlockType[]>((acc, b) => {
  if (!isContainerBlockType(b.type)) {
    acc.push(b.type);
  }
  return acc;
}, []);

// Map leaf block types to icons for the child block list
export const leafBlockIcons: Record<LeafBlock["type"], LucideIcon> = {
  text: Type,
  heading: Heading,
  button: MousePointerClick,
  card: Square,
  spacer: Space,
  divider: Minus,
  banner: Megaphone,
  html: Code,
  "rich-text": Type,
  popup: MessageSquare,
  "appointment-list": Calendar,
  "order-list": Package,
  "chatroom-list": MessageCircle,
  "file-list": FileText,
  "form-response-list": ClipboardList,
  "pinned-forms-list": NotebookPen,
  "profile-edit": UserPen,
  "content-list": BookOpen,
  "purchase-list": CreditCard,
  "file-upload": Upload,
  "stripe-subscriptions": Zap,
  "stripe-billing-portal-button": Receipt,
  "stripe-invoices": Receipt,
  "appointment-booking": CalendarPlus,
  "vital-data": Activity,
  "weight-macro-tracker": Weight,
  shop: ShoppingBag,
};

// Map leaf block types to display labels
export const getLeafBlockLabels: () => Record<LeafBlock["type"], string> = () => ({
  text: i18n.t("Text", { ns: "common" }),
  heading: i18n.t("Heading", { ns: "common" }),
  button: i18n.t("Button", { ns: "common" }),
  card: i18n.t("Card", { ns: "common" }),
  spacer: i18n.t("Spacer", { ns: "common" }),
  divider: i18n.t("Divider", { ns: "common" }),
  banner: i18n.t("Banner", { ns: "common" }),
  html: i18n.t("HTML", { ns: "common" }),
  "rich-text": i18n.t("Rich Text", { ns: "common" }),
  popup: i18n.t("Popup", { ns: "common" }),
  "appointment-list": i18n.t("Appointments", { ns: "common" }),
  "order-list": i18n.t("Orders", { ns: "common" }),
  "chatroom-list": i18n.t("Messages", { ns: "common" }),
  "file-list": i18n.t("Files", { ns: "common" }),
  "form-response-list": i18n.t("Form Responses", { ns: "common" }),
  "pinned-forms-list": i18n.t("Forms", { ns: "common" }),
  "profile-edit": i18n.t("Profile Edit", { ns: "common" }),
  "content-list": i18n.t("Content", { ns: "common" }),
  "purchase-list": i18n.t("Purchases", { ns: "common" }),
  "file-upload": i18n.t("File Upload", { ns: "common" }),
  "stripe-subscriptions": i18n.t("Stripe Subscriptions", { ns: "common" }),
  "stripe-billing-portal-button": i18n.t("Stripe Billing Portal", { ns: "common" }),
  "stripe-invoices": i18n.t("Stripe Invoices", { ns: "common" }),
  "appointment-booking": i18n.t("Book Appointment", { ns: "common" }),
  "vital-data": i18n.t("Vital Data", { ns: "common" }),
  "weight-macro-tracker": i18n.t("Weight & Macro Tracker", { ns: "common" }),
  shop: i18n.t("Shop", { ns: "shop" }),
});

// Preferred order for appointment categories (used when adding new categories)
export const APPOINTMENT_CATEGORY_PREFERRED_ORDER: AppointmentCategory[] = ["upcoming", "past", "all"];

// Category labels and descriptions for appointments
export const getAppointmentCategoryInfo: () => Record<
  AppointmentCategory,
  { label: string; description?: string }
> = () => ({
  upcoming: {
    label: i18n.t("Upcoming", { ns: "common" }),
    description: i18n.t("within 12 hours past to future", { ns: "common" }),
  },
  past: { label: i18n.t("Past", { ns: "common" }) },
  all: { label: i18n.t("All", { ns: "common" }) },
});

// Preferred order for form response categories
export const FORM_RESPONSE_CATEGORY_PREFERRED_ORDER: FormResponseCategory[] = ["submitted", "unsubmitted", "all"];

// Category labels and descriptions for form responses
export const getFormResponseCategoryInfo: () => Record<
  FormResponseCategory,
  { label: string; description?: string }
> = () => ({
  submitted: { label: i18n.t("Submitted", { ns: "common" }), description: i18n.t("completed forms", { ns: "common" }) },
  unsubmitted: {
    label: i18n.t("Pending", { ns: "common" }),
    description: i18n.t("needs completion", { ns: "common" }),
  },
  all: { label: i18n.t("All", { ns: "common" }) },
});

// Profile field labels for the editor
export const getProfileFieldLabels: () => Record<ProfileFieldKey, string> = () => ({
  fname: i18n.t("First Name", { ns: "common" }),
  lname: i18n.t("Last Name", { ns: "common" }),
  email: i18n.t("Email", { ns: "common" }),
  phone: i18n.t("Phone", { ns: "common" }),
  addressLineOne: i18n.t("Address Line 1", { ns: "common" }),
  addressLineTwo: i18n.t("Address Line 2", { ns: "common" }),
  city: i18n.t("City", { ns: "common" }),
  state: i18n.t("State", { ns: "common" }),
  zipCode: i18n.t("ZIP Code", { ns: "common" }),
});

// All profile field keys in display order
export const PROFILE_FIELD_KEYS: ProfileFieldKey[] = [
  "fname",
  "lname",
  "email",
  "phone",
  "addressLineOne",
  "addressLineTwo",
  "city",
  "state",
  "zipCode",
];

// Fields that should default to not required (must match useProfileEdit.ts)
export const NON_REQUIRED_BY_DEFAULT: ProfileFieldKey[] = ["addressLineTwo"];
